import { ref, reactive, onMounted, nextTick } from 'vue'
import { getUserScopedStorageKey } from '@/utils/userScopedStorage'
import { mergeColumnConfigWithSavedOrder } from '@/utils/mergeColumnConfig'

/**
 * 列宽管理组合式函数
 * @param {string} storageKey - 本地存储键名
 * @param {Object} defaultColumnWidths - 默认列宽配置
 * @param {Object} columnMap - 列映射配置
 * @returns {Object} 列宽管理相关方法和数据
 */
export default function useColumnWidth(storageKey, defaultColumnWidths, columnMap = null) {
  const scopedPrefix = () => getUserScopedStorageKey(storageKey)
  // 列宽配置响应式对象
  const columnWidths = reactive({ ...defaultColumnWidths })
  
  // 表格引用
  const tableRef = ref(null)

  /**
   * 加载列宽设置
   */
  const loadColumnWidths = () => {
    try {
      const savedWidths = localStorage.getItem(`${scopedPrefix()}_columns_width`)
      if (savedWidths) {
        const parsedWidths = JSON.parse(savedWidths)
        Object.keys(defaultColumnWidths).forEach(key => {
          if (parsedWidths[key]) {
            columnWidths[key] = parsedWidths[key]
          }
        })
      }
    } catch (error) {
      console.error(`加载列宽设置失败 (${storageKey}):`, error)
    }
  }

  /**
   * 保存列宽设置
   */
  const saveColumnWidths = () => {
    try {
      localStorage.setItem(`${scopedPrefix()}_columns_width`, JSON.stringify(columnWidths))
    } catch (error) {
      console.error(`保存列宽设置失败 (${storageKey}):`, error)
    }
  }

  /**
   * 列宽调整处理
   * Element Plus 的 header-dragend 事件参数格式: (column, newWidth, oldWidth)
   * 使用防抖和 requestAnimationFrame 避免 ResizeObserver 循环错误
   */
  let resizeTimer = null
  let resizeDebounceTimer = null
  
  const handleColumnResize = (...args) => {
    // 清除之前的定时器
    if (resizeTimer) {
      cancelAnimationFrame(resizeTimer)
      resizeTimer = null
    }
    if (resizeDebounceTimer) {
      clearTimeout(resizeDebounceTimer)
      resizeDebounceTimer = null
    }

    // Element Plus header-dragend 事件参数: (column, newWidth, oldWidth)
    let column, newWidth
    if (args.length >= 3) {
      [column, newWidth] = args
    } else if (args.length === 1 && args[0]) {
      // 如果只有一个参数，可能是 column 对象
      column = args[0]
      newWidth = column.width || column.realWidth
    }

    // 使用双重延迟：requestAnimationFrame + setTimeout，避免 ResizeObserver 循环
    resizeTimer = requestAnimationFrame(() => {
      resizeDebounceTimer = setTimeout(() => {
        try {
          // 优先使用 column.property 和 newWidth，避免读取 DOM
          if (column && column.property && newWidth && newWidth > 0) {
            const prop = column.property
            // 检查属性是否在默认配置中
            if (prop in defaultColumnWidths) {
              // 使用 nextTick 确保在 DOM 更新后保存
              nextTick(() => {
                columnWidths[prop] = newWidth
                saveColumnWidths()
              })
              return
            }
          }

          // 降级方案：如果没有 property，尝试从列映射中查找
          if (column && column.label && columnMap) {
            const prop = columnMap[column.label]
            if (prop && newWidth && newWidth > 0) {
              nextTick(() => {
                columnWidths[prop] = newWidth
                saveColumnWidths()
              })
              return
            }
          }

          // 最后降级方案：从 DOM 读取（使用更安全的方式，延迟更长时间）
          if (tableRef.value && tableRef.value.$el && columnMap) {
            // 延迟读取，避免触发 ResizeObserver
            setTimeout(() => {
              try {
                const tableEl = tableRef.value.$el
                const headerCells = tableEl.querySelectorAll('.el-table__header .el-table__cell')

                headerCells.forEach((cell) => {
                  const headerText = cell.querySelector('.cell')?.textContent?.trim()
                  if (headerText && columnMap[headerText]) {
                    // 使用 getBoundingClientRect 而不是 offsetWidth
                    const width = Math.round(cell.getBoundingClientRect().width)
                    if (width > 0) {
                      columnWidths[columnMap[headerText]] = width
                    }
                  }
                })
                saveColumnWidths()
              } catch (error) {
                // 静默处理错误，避免触发更多 ResizeObserver 错误
                if (!error.message || !error.message.includes('ResizeObserver')) {
                  console.error('从 DOM 读取列宽失败:', error)
                }
              }
            }, 300) // 增加延迟时间
          }
        } catch (error) {
          // 静默处理 ResizeObserver 相关错误
          if (!error.message || !error.message.includes('ResizeObserver')) {
            console.error('处理列宽调整失败:', error)
          }
        }
      }, 50) // 添加额外的延迟
    })
  }

  /**
   * 重置列宽为默认值
   */
  const resetColumnWidths = () => {
    Object.keys(defaultColumnWidths).forEach(key => {
      columnWidths[key] = defaultColumnWidths[key]
    })
    saveColumnWidths()
  }

  /**
   * 获取列配置
   */
  const getColumnConfig = (defaultConfig) => {
    try {
      const savedConfig = localStorage.getItem(`${scopedPrefix()}_columns_config`)
      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig)
        return mergeColumnConfigWithSavedOrder(defaultConfig, parsedConfig)
      }
    } catch (error) {
      console.error('加载列配置失败:', error)
    }
    return defaultConfig
  }

  // 在组件挂载后加载列宽设置
  onMounted(() => {
    nextTick(() => {
      loadColumnWidths()
    })
  })

  return {
    tableRef,
    columnWidths,
    loadColumnWidths,
    saveColumnWidths,
    handleColumnResize,
    resetColumnWidths,
    getColumnConfig
  }
}